/* eslint-disable no-spaced-func */
import React from 'react';
import { Card } from '@frontend/shadcn/components/ui/card';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@frontend/shadcn/components/ui/avatar';
import { InstagramIcon, TiktokIcon, TwitterIcon, FacebookIcon, YoutubeIcon } from '@revfluence/fresh-icons/brands/esm';
import { cn } from '@frontend/shadcn/lib/utils';
import { getColorFromName, getInitials } from '@frontend/app/refresh-components/NameCell';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { ImageIcon } from '@revfluence/fresh-icons/regular/esm';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';

/* -------------------------- Media Sub-Components -------------------------- */

export const PostCard = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <Card ref={ref} className={cn('relative w-full aspect-[9/16] group overflow-hidden', className)} {...props}>
      {children}
    </Card>
  ),
);
PostCard.displayName = 'PostCard';

export const PostCardMedia = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('absolute inset-0', className)} {...props}>
      {children}
    </div>
  ),
);
PostCardMedia.displayName = 'PostCardMedia';

export const PostCardSkeleton = () => <Skeleton className="w-full h-full" />;

export const PostCardBackground = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { imageUrl: string }
>(({ className, imageUrl, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('absolute inset-0 w-full h-full bg-cover bg-center blur-md brightness-90 scale-110', className)}
    style={{
      backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
    }}
    {...props}
  />
));
PostCardBackground.displayName = 'PostCardBackground';
export const PostCardImageContainer = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('relative h-full min-h-28 min-w-28 overflow-hidden rounded-lg md:min-h-36 md:min-w-36', className)}
      {...props}
    >
      {children}
    </div>
  ),
);
PostCardImageContainer.displayName = 'PostCardImageContainer';

export const PostCardImage = React.forwardRef<React.ElementRef<'img'>, React.ComponentPropsWithoutRef<'img'>>(
  ({ className, ...props }, ref) => {
    return <img alt="Post content" className={cn('w-full h-full object-contain', className)} {...props} ref={ref} />;
  },
);
PostCardImage.displayName = 'PostCardImage';

export const PostCardImageContent = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('relative h-full w-full', className)} {...props}>
      {children}
    </div>
  ),
);
PostCardImageContent.displayName = 'PostCardImageContent';

export const PostCardPlaceholder = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('w-full h-full bg-slate-800 flex items-center justify-center', className)} {...props}>
      <ImageIcon className="h-16 w-16 text-slate-500" />
    </div>
  ),
);
PostCardPlaceholder.displayName = 'PostCardPlaceholder';

export const PostCardGradient = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'absolute inset-0 bg-gradient-to-b from-[#16282D]/80 via-[#16282D]/20 to-[#16282D]/80 pointer-events-none z-[2]',
        className,
      )}
      {...props}
    />
  ),
);
PostCardGradient.displayName = 'PostCardGradient';

/* -------------------------- Content Sub-Components -------------------------- */

export const PostCardContent = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('relative h-full flex flex-col z-10', className)} {...props}>
      {children}
    </div>
  ),
);
PostCardContent.displayName = 'PostCardContent';

export const PostCardHeader = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('p-4 flex items-center gap-3', className)} {...props}>
      {children}
    </div>
  ),
);
PostCardHeader.displayName = 'PostCardHeader';

export const PostCardAvatarContainer = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-3', className)} {...props}>
      {children}
    </div>
  ),
);
PostCardAvatarContainer.displayName = 'PostCardAvatarContainer';

export const PostCardNetworkIcon = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { network: string }
>(({ className, network, ...props }, ref) => {
  const getNetworkIcon = () => {
    const networkType = network.toLowerCase();
    switch (networkType) {
      case 'instagram':
        return <InstagramIcon className="h-full w-full" />;
      case 'twitter':
      case 'x':
        return <TwitterIcon className="h-full w-full" />;
      case 'facebook':
        return <FacebookIcon className="h-full w-full" />;
      case 'tiktok':
        return <TiktokIcon className="h-full w-full" />;
      case 'youtube':
        return <YoutubeIcon className="h-full w-full" />;
      default:
        return null;
    }
  };

  return (
    <div
      ref={ref}
      className={cn(
        'absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-white flex items-center justify-center p-1',
        className,
      )}
      {...props}
    >
      {getNetworkIcon()}
    </div>
  );
});
PostCardNetworkIcon.displayName = 'PostCardNetworkIcon';

export const PostCardAvatar = React.forwardRef<
  React.ElementRef<typeof Avatar>,
  React.ComponentPropsWithoutRef<typeof Avatar> & {
    networkIcon?: React.ReactNode;
  }
>(({ className, children, networkIcon, ...props }, ref) => (
  <div className="relative">
    <Avatar ref={ref} className={cn('h-8 w-8 ring-2 ring-white', className)} {...props}>
      {children}
    </Avatar>
    {networkIcon}
  </div>
));
PostCardAvatar.displayName = 'PostCardAvatar';

export const PostCardAvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarImage>,
  React.ComponentPropsWithoutRef<typeof AvatarImage> & { alt: string }
>(({ src, alt, ...props }, ref) => <AvatarImage ref={ref} src={src || '/default-avatar.png'} alt={alt} {...props} />);
PostCardAvatarImage.displayName = 'PostCardAvatarImage';

export const PostCardAvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarFallback>,
  React.ComponentPropsWithoutRef<typeof AvatarFallback> & { name: string }
>(({ name, className, ...props }, ref) => (
  <AvatarFallback
    ref={ref}
    className={cn(
      'flex items-center justify-center h-full w-full text-white font-semibold text-[10px]',
      getColorFromName(name),
      className,
    )}
    {...props}
  >
    {getInitials(name)}
  </AvatarFallback>
));
PostCardAvatarFallback.displayName = 'PostCardAvatarFallback';

export const PostCardAvatarName = React.forwardRef<React.ElementRef<'span'>, React.ComponentPropsWithoutRef<'span'>>(
  ({ className, children, ...props }, ref) => (
    <span ref={ref} className={cn('font-medium text-white drop-shadow-sm', className)} {...props}>
      {children}
    </span>
  ),
);
PostCardAvatarName.displayName = 'PostCardAvatarName';

export const PostCardFooter = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('p-4 space-y-3', className)} {...props}>
      {children}
    </div>
  ),
);
PostCardFooter.displayName = 'PostCardFooter';

export const PostCardNetwork = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { network: string }
>(({ className, network, ...props }, ref) => (
  <div ref={ref} className={cn(className)} {...props}>
    <Badge variant="secondary" className="capitalize bg-white/20 text-white">
      {network.toLowerCase()}
    </Badge>
  </div>
));
PostCardNetwork.displayName = 'PostCardNetwork';

export const PostCardActions = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center justify-between text-sm text-white', className)} {...props}>
      {children}
    </div>
  ),
);
PostCardActions.displayName = 'PostCardActions';

export const PostCardLink = React.forwardRef<React.ElementRef<'a'>, React.ComponentPropsWithoutRef<'a'>>(
  ({ className, children, ...props }, ref) => (
    <a
      ref={ref}
      className={cn('text-white hover:underline', className)}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </a>
  ),
);
PostCardLink.displayName = 'PostCardLink';

export const PostCardStats = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-4', className)} {...props}>
      {children}
    </div>
  ),
);
PostCardStats.displayName = 'PostCardStats';

export const PostCardStat = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { icon: React.ReactNode; value: number }
>(({ className, icon, value, ...props }, ref) => {
  const formatter = new Intl.NumberFormat('en', { notation: 'compact' });

  return (
    <div ref={ref} className={cn('flex items-center gap-1', className)} {...props}>
      {icon}
      <span>{formatter.format(value)}</span>
    </div>
  );
});
PostCardStat.displayName = 'PostCardStat';

export const PostCardHeaderRow = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn('flex w-full items-center justify-between', className)} {...props}>
      {children}
    </div>
  ),
);
PostCardHeaderRow.displayName = 'PostCardHeaderRow';

export const PostCardCheckbox = React.forwardRef<
  React.ElementRef<typeof Checkbox>,
  React.ComponentPropsWithoutRef<typeof Checkbox>
>(({ className, ...props }, ref) => (
  <Checkbox
    ref={ref}
    className={cn('h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 bg-white', className)}
    {...props}
  />
));
PostCardCheckbox.displayName = 'PostCardCheckbox';

export default PostCard;
